import { useEffect, useContext, useState } from 'react';
import { Box, Card, CardContent, Grid, Paper, Stack, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

import ShowChartIcon from "@mui/icons-material/ShowChart";


import CompanyContext 		from "../context";
import useFetch 			from "../../../api/api";
import useExternalFetch 	from '../../../api/apiExternal';
import useAuth 				from '../../../hooks/useAuth';
import config 				from '../../../common/Config';
import { formatCurrency } 	from '../../../common/Helper';

import LoadingCard 			from '../../Dashboard/components/LoadingCard';
import RestrictedAccess		from '../../../components/RestrictedAccess';

const colors				= ['#FFC0CB','#FF8C00','#BA55D3','#32CD32','#40E0D0','#00BFFF','#696969','#F4A460']

const TabCharts = () => {
	const { company, stocks, selectedStock, indicators, currency, exchangeRate }	= useContext(CompanyContext)
	const { auth }												= useAuth();
	const privateFetch											= useFetch()
	const externalFetch											= useExternalFetch()

	const [economicIndicators, setEconomicIndicators]			= useState()
	const [historicPriceEarnings, setHistoricPriceEarnings]		= useState()
	const [stockPrices, setStockPrices]							= useState()
	const [financialReports, setFinancialReports]				= useState()
	const [isLoading, setIsLoading]								= useState(true)

	const currentFinancialReports 								= []

	useEffect(() => {
		if (company && selectedStock && auth.userId) {
			// Busca indicadores de PIB e Inflacao de acordo com o pais (BR ou USA)
			const promise1 = privateFetch(config.apiUrl +"/api/indicators/"+ company.country)
			.then((data) => data.json())
			.then((data) => setEconomicIndicators(data))

			// Busca o historico de cotacoes e cria um array com Data, VPA, Graham e Preço
			const promise2 = externalFetch('https://financialmodelingprep.com/api/v3/historical-price-full/'+ selectedStock + ( selectedStock.match(/.*[0-9]$/) ? '.SA' : '' ) +'?serietype=line')
			.then(data => data.json())
			.then(data => {
				let stockPriceList = []
				let financialReportList = []
				let convertRate = company.country !== 'BR'&& company.country !== 'US' ? exchangeRate : 1
				
				company.financialReports.map(fr => {
					let price			= data.historical.find(d => new Date(d.date).getFullYear() === new Date(fr.date).getFullYear())
					let grahamNumber	= (fr ? Math.sqrt(22.5 * (fr.bookValue * convertRate) * (fr.eps * convertRate)).toFixed(2) : 0)

					stockPriceList.push({
						date 					: new Date(fr.date).getFullYear(),
						bookValue				: fr?.bookValue ? fr.bookValue * convertRate : 0,
						graham					: isNaN(grahamNumber) ? 0 : grahamNumber,
						close					: price ? price.close : 0
					})

					financialReportList.push({
						date 					: new Date(fr.date).getFullYear(),
						netIncome				: fr?.netIncome ? fr.netIncome * convertRate : 0,	
						eps						: fr?.eps 		? fr.eps * convertRate : 0,
						revenue					: fr?.revenue 	? fr.revenue * convertRate : 0
					})
				})

				setStockPrices(stockPriceList)
				setFinancialReports(financialReportList)
			})

			// Busca P/L historico
			const currentIndex = company.country === 'BR' ? 'IBOV' : 'SP500'
			const promise3 = privateFetch(config.apiUrl +"/api/indicators/historicPE/"+ currentIndex)
			.then((data) => data.json())
			.then((data) => { 
				if (data.message || data.status === 500 ) {
					setHistoricPriceEarnings([])
					return
				}

				let stockRatios = stocks.find(s => s.ticker === selectedStock).stockRatios

				stockRatios.map(ratio => {
					let index = data.findIndex(d => d.year === new Date(ratio.date).getFullYear());
					if (index >= 0) data[index][selectedStock] = ratio.priceEarnings
				})

				setHistoricPriceEarnings(data.filter(d => d[selectedStock] > 0))}
			)
			.catch(
				setHistoricPriceEarnings([])
			)			

			Promise.all([promise1, promise2, promise3])
			.then(() => setIsLoading(false))
		}
		else {
			setIsLoading(false)
		}
	}, [company, selectedStock])

	if (!company || !indicators || isLoading) {
		return (<LoadingCard/>)
	}

	return (
		auth.userId ? (
			<Grid container>
				<Grid item xs={12} sm={6} pb={5}>
					<Typography variant='h6' align="center">Receita, Lucro Liquido e Lucro por Ação</Typography>
					<ResponsiveContainer width={'100%'} height={300}>
						<LineChart data={financialReports.sort((a,b) => new Date(a.date) - new Date(b.date))}>
							<CartesianGrid strokeDasharray="1 2"/>
							<XAxis dataKey="date" style={{fontSize: 10}} />
							<YAxis yAxisId="left" style={{fontSize: 10}} />
							<YAxis 
								yAxisId="right" 
								style={{fontSize: 10}} 
								orientation="right"
								tickFormatter={value => value.toFixed(2)}
								domain={[dataMin => dataMin - (Math.abs(dataMin) * 0.1), dataMax => dataMax * 1.1]}
							/>
							<Tooltip content={<CustomTooltip caption={company.country !== 'BR' && company.country !== 'US' ? '* Os resultados de todos os períodos foram convertidos para USD utilizando a cotação de hoje e devem ser utilizados apenas como estimativa.' : null}/>}/>
							<Legend wrapperStyle={{fontSize: "12px"}} iconType='circle' iconSize={8}/>
							<Line yAxisId="left" name="Receita" type="monotone" dataKey="revenue" stroke="#8884d8" dot={false} unit={company.country === 'BR' ? "$_BIG_BRL" : '$_BIG_USD'}/>
							<Line yAxisId="left" name="Lucro Líquido" type="monotone" dataKey="netIncome" stroke="#82ca9d" dot={false} unit={company.country === 'BR' ? "$_BIG_BRL" : '$_BIG_USD'}/>
							<Line yAxisId="right" name="Lucro por Ação" connectNulls type="monotone" dataKey={(data) => new Number(data.eps)} stroke="#FF5733" dot={false} unit={company.country === 'BR' ? "$_SMALL_BRL" : '$_SMALL_USD'}/>
						</LineChart>
					</ResponsiveContainer>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Typography variant='h6' align="center">Cotação vs VPA vs Graham</Typography>
					<ResponsiveContainer width={'100%'} height={300}>
						<LineChart data={stockPrices.sort((a,b) => new Date(a.date) - new Date(b.date))}>
						<CartesianGrid strokeDasharray="1 2"/>
						<XAxis dataKey="date" style={{fontSize: 10}} />
							<YAxis 
								yAxisId="left" 
								style={{fontSize: 10}} 
								orientation="left" 
								tickFormatter={(item) => item.toFixed(2)}
							/>
							<YAxis 
								yAxisId="right" 
								style={{fontSize: 10}} 
								orientation="right" 
								domain={['auto', 'auto']}
							/>
							<Tooltip content={<CustomTooltip caption={company.country !== 'BR' && company.country !== 'US' ? '* O VPA o Graham foram obtidos convertendo os valores para USD utilizando a cotação de hoje e devem ser utilizados apenas como estimativa.' : null}/>} />
							<Legend wrapperStyle={{fontSize: "12px"}} iconType='circle' iconSize={8}/>				
							<Line yAxisId="left" name="Cotação" type="monotone" dataKey="close" stroke={colors[0]} dot={false} unit={company.country === 'BR' ? "$_SMALL_BRL" : '$_SMALL_USD'}/>
							<Line yAxisId="left" name="VPA" type="monotone" dataKey="bookValue" stroke="#82ca9d" dot={false} unit={company.country === 'BR' ? "$_SMALL_BRL" : '$_SMALL_USD'}/>
							<Line yAxisId="left" name="Graham" type="monotone" dataKey="graham" stroke="#FF33FF" dot={false} unit={company.country === 'BR' ? "$_SMALL_BRL" : '$_SMALL_USD'}/>
						</LineChart>
					</ResponsiveContainer>			
				</Grid>
				<Grid item xs={12} sm={6}>
					<Typography variant='h6' align="center">PIB e Inflação </Typography>
					{!economicIndicators || economicIndicators.length === 0 ? (
						<NoDataForChart text={'Não há dados de inflação e PIB para o país desta empresa.'}/> 
					) : (
						<ResponsiveContainer width={'100%'} height={300}>
							{/* Filtra indicadores economicos maiores ou iguais aos relatorio financeiros para ter mais ou menos a mesma escala para comparação */}
							<LineChart data={economicIndicators.filter(e => e.date >= Math.min(...financialReports.map(f => f.date))).sort((a,b) => a.date - b.date)}>
								<CartesianGrid strokeDasharray="1 2"/>
								<XAxis dataKey="date" style={{fontSize: 10}} />
								<YAxis yAxisId="left" style={{fontSize: 10}} tickFormatter={(item) => Math.round(parseFloat(item)/10000)} domain={[Math.min(...economicIndicators.map(ei => ei.growth)),Math.max(...economicIndicators.map(ei => ei.growth))*1.1]}/>
								<YAxis yAxisId="right" style={{fontSize: 10}} tickFormatter={(item) => Math.round(parseFloat(item))} orientation="right" domain={[Math.min(...economicIndicators.map(ei => ei.inflation)),Math.max(...economicIndicators.map(ei => ei.inflation))*1.1]}/>
								<Tooltip content={<CustomTooltip/>} />
								<Legend wrapperStyle={{fontSize: "12px"}} iconType='circle' iconSize={8}/>
								<Line yAxisId="left" name="PIB" type="monotone" dataKey="growth" stroke="#8884d8" dot={false} unit={"#_BIG"}/>
								<Line yAxisId="right" name="Inflação" type="monotone" dataKey="inflation" stroke="#82ca9d" dot={false} unit={"#_BIG"}/>
							</LineChart>
						</ResponsiveContainer>
					) }

				</Grid>			
				<Grid item xs={12} sm={6}>
					<Typography variant='h6' align="center">P/L {company.country === 'BR' ? 'IBOV' : 'SP500'} vs P/L {selectedStock}</Typography>
					<ResponsiveContainer width={'100%'} height={300}>
						{historicPriceEarnings && (
							<LineChart data={historicPriceEarnings}>
								<CartesianGrid strokeDasharray="1 2"/>
								<XAxis dataKey="year" style={{fontSize: 10}} />
								<YAxis 
									yAxisId="left" 
									orientation="left" 
									style={{fontSize: 10}} 
									tickFormatter={(item) => item.toFixed(2)}
									domain={[0,Math.max(...historicPriceEarnings.map(hpe => Math.max(hpe.priceEarnings, hpe[selectedStock] ? hpe[selectedStock] : 100)))*1.1]}
								/>
								<Tooltip content={<CustomTooltip caption={'O P/L do '+ (company.country === 'BR' ? 'IBOV' : 'SP500') +' foi obtido através da mediana das empresas que compões o índice.'}/>}/>
								<Legend wrapperStyle={{fontSize: "12px"}} iconType='circle' iconSize={8}/>
								<Line yAxisId="left" type="monotone" name={"P/L "+ (company.country === 'BR' ? 'IBOV' : 'SP500')} dataKey="priceEarnings" stroke="#82ca9d" dot={false} unit={company.country === 'BR' ? "#_2" : '#_2'}/>
								<Line yAxisId="left" type="monotone" name={"P/L "+ selectedStock} dataKey={data => new Number(data[selectedStock])} stroke={colors[1]} dot={false} connectNulls  unit={company.country === 'BR' ? "#_2" : '#_2'}/>
							</LineChart>
						)}
					</ResponsiveContainer>
				</Grid>			
			</Grid>
		) : (
			<Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '78vh'}}>
				<RestrictedAccess />
			</Box>
		)
	)
}

const CustomTooltip = (props) => {

	const { active, payload, label, dataType, caption } = props;
	
	const formatValue = (pValue, pFormat) => {
		let formattedValue; 
		let formatParameters = pFormat.split('_')

		switch (formatParameters[0]) {
			case '$' 	: formattedValue = formatCurrency(pValue, formatParameters[1], formatParameters[2]); break;
			case '#' 	: formattedValue = formatParameters[1] === "2" ? parseFloat(pValue).toFixed(2) : Math.round(pValue); break;
		}

		return formattedValue
	}

	return (
		<Card sx={{width: 200}}>
			<CardContent>
				<Stack spacing={1}>
					<Typography align="center" sx={{fontWeight:"bold"}}>Posição em {label}</Typography>
					<Table size="small">
						<TableBody>
						{payload.map(p => (
							<TableRow key={"key_"+ p.name}>
								<TableCell sx={{color: p.stroke}}>{p.name}</TableCell>
								<TableCell sx={{color: p.stroke}}>{formatValue(p.value, p.unit)}</TableCell>
							</TableRow>
						))}
						</TableBody>
					</Table>
					{ caption ? (
						<Typography pt={1} variant='caption'>{caption}</Typography>
					) : null}
					
				</Stack>
			</CardContent>
		</Card>
	)
}

const NoDataForChart = (props) => {
	return (
		<Box sx={{ px: 5}}>
		<Paper
			elevation={0}
			sx={{
			height: 250,
			display: "flex",
			flexDirection: "column",
			justifyContent: "center",
			alignItems: "center",
			color: "text.secondary",
			border: "1px dashed",
			borderColor: "divider",
			}}
			>
			<ShowChartIcon sx={{ fontSize: 40, mb: 1, color: "text.disabled" }} />
			<Typography variant="h6">Nenhum dado disponível</Typography>
			<Typography variant="body2">{props.text || 'Aguarde ou selecione outro período'}</Typography>
		</Paper>
		</Box>
    );
}



export default TabCharts